import { ArrowDownRight, ArrowUpRight, ChevronDown, ChevronLeft, ChevronRight, ChevronUp, Star, type LucideIcon } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import type { Order, OrderStatus, PaymentMethod, User } from '../../../shared/types';

const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

export interface OverviewStatCard {
  id: string;
  title: string;
  value: string;
  trendValue: string;
  trendLabel: string;
  isPositive: boolean;
  icon: LucideIcon;
}

export interface OverviewTopPageRow {
  path: string;
  pageViews: number;
  exitRate: number;
}

export interface OverviewSideListItem {
  id: string;
  title: string;
  subtitle?: string;
  imageUrl?: string;
  href?: string;
  rating?: number;
  reviewCount?: number;
}

interface OverviewDashboardProps {
  greeting: string;
  alertMessage: string;
  statCards: OverviewStatCard[];
  performanceBars: number[];
  performanceLine: number[];
  conversionRate?: number;
  thisWeekValue?: string;
  lastWeekValue?: string;
  topPages?: OverviewTopPageRow[];
  recentOrders: Order[];
  users?: User[];
  action?: ReactNode;
  tone?: 'light' | 'night';
  variant?: 'classic' | 'neo';
  showUserDistribution?: boolean;
  showRecentUsers?: boolean;
  showRecentOrders?: boolean;
  sideListTitle?: string;
  sideListItems?: OverviewSideListItem[];
}

const statusClasses: Record<OrderStatus, string> = {
  Pending: 'bg-slate-100 text-slate-700',
  Paid: 'bg-emerald-100 text-emerald-700',
  Processed: 'bg-amber-100 text-amber-700',
  Shipped: 'bg-sky-100 text-sky-700',
  'En Route': 'bg-indigo-100 text-indigo-700',
  Arrived: 'bg-emerald-100 text-emerald-700',
  Cancelled: 'bg-rose-100 text-rose-700',
  Failed: 'bg-rose-100 text-rose-700',
};

const paymentLabel: Record<PaymentMethod, string> = {
  card: 'Credit Card',
  cod: 'Cash on Delivery',
  paypal: 'PayPal',
  mobileMoney: 'Mobile Money',
};

const formatOrderDate = (dateValue: string): string => {
  const parsedDate = new Date(dateValue);
  if (Number.isNaN(parsedDate.getTime())) {
    return '-';
  }

  return parsedDate.toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' });
};

const formatCompactNumber = (value: number): string =>
  new Intl.NumberFormat('en-US', { notation: 'compact', maximumFractionDigits: 1 }).format(value);

const toFixedSeries = (values: number[], length: number): number[] => {
  if (values.length >= length) {
    return values.slice(0, length);
  }

  const filled = [...values];
  while (filled.length < length) {
    filled.push(0);
  }
  return filled.slice(0, length);
};

type Point = { x: number; y: number };

const buildSmoothPath = (points: Point[]): string => {
  if (points.length === 0) return '';
  if (points.length === 1) return `M ${points[0].x} ${points[0].y}`;
  if (points.length === 2) return `M ${points[0].x} ${points[0].y} L ${points[1].x} ${points[1].y}`;

  let d = `M ${points[0].x} ${points[0].y}`;
  for (let index = 1; index < points.length - 2; index += 1) {
    const current = points[index];
    const next = points[index + 1];
    const controlX = (current.x + next.x) / 2;
    const controlY = (current.y + next.y) / 2;
    d += ` Q ${current.x} ${current.y} ${controlX} ${controlY}`;
  }
  const penultimate = points[points.length - 2];
  const last = points[points.length - 1];
  d += ` Q ${penultimate.x} ${penultimate.y} ${last.x} ${last.y}`;
  return d;
};

const buildLinePoints = (values: number[], width: number, height: number, padding: number): Point[] => {
  const safeValues = values.map((value) => (Number.isFinite(value) ? value : 0));
  const maxValue = Math.max(...safeValues, 1);
  const xStep = (width - padding * 2) / Math.max(1, safeValues.length - 1);
  const ySpan = height - padding * 2;

  return safeValues.map((value, index) => {
    const x = padding + index * xStep;
    const normalized = Math.max(0, value) / maxValue;
    const y = height - padding - normalized * ySpan;
    return { x: Number(x.toFixed(2)), y: Number(y.toFixed(2)) };
  });
};

const buildAxisTicks = (maxValue: number, count = 4): Array<{ value: number; label: string; fraction: number }> => {
  const resolvedCount = Math.max(2, Math.min(6, Math.floor(count)));
  const safeMax = Number.isFinite(maxValue) && maxValue > 0 ? maxValue : 1;
  return Array.from({ length: resolvedCount }, (_, index) => {
    const fraction = index / (resolvedCount - 1);
    const value = safeMax * fraction;
    return { value, label: formatCompactNumber(value), fraction };
  }).reverse();
};

const MiniSparkline = ({
  values,
  stroke,
  baselineStroke,
}: {
  values: number[];
  stroke: string;
  baselineStroke: string;
}) => {
  const resolved = toFixedSeries(values, 12);
  const points = buildLinePoints(resolved, 100, 30, 4);
  const path = buildSmoothPath(points);
  return (
    <svg viewBox="0 0 100 30" className="h-9 w-24" aria-hidden="true">
      <path d="M 4 26 H 96" stroke={baselineStroke} strokeWidth="1" fill="none" />
      <path
        d={path}
        className="haapa-sparkline-line"
        stroke={stroke}
        strokeWidth="2.2"
        fill="none"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
};

const NeoOverviewDashboard = ({
  greeting,
  alertMessage,
  statCards,
  performanceBars,
  performanceLine,
  recentOrders,
  users,
  action,
  showRecentUsers,
  showRecentOrders,
  sideListTitle,
  sideListItems,
  tone = 'light',
}: OverviewDashboardProps) => {
  const resolvedUsers = users ?? [];
  const carouselRef = useRef<HTMLDivElement | null>(null);
  const salesChartRef = useRef<SVGSVGElement | null>(null);
  const nomineesRef = useRef<HTMLDivElement | null>(null);
  const [isCarouselHovered, setIsCarouselHovered] = useState(false);
  const [isNomineesHovered, setIsNomineesHovered] = useState(false);
  const cards = statCards.slice(0, 4);
  const salesSeries = useMemo(() => toFixedSeries(performanceBars, 12), [performanceBars]);
  const volumeSeries = useMemo(() => toFixedSeries(performanceLine, 12), [performanceLine]);
  const ordersByBuyerId = useMemo(() => {
    const counts = new Map<string, number>();
    recentOrders.forEach((order) => {
      if (!order.buyerId) return;
      counts.set(order.buyerId, (counts.get(order.buyerId) ?? 0) + 1);
    });
    return counts;
  }, [recentOrders]);
  const maxSalesValue = Math.max(...salesSeries, 1);
  const maxVolumeValue = Math.max(...volumeSeries, 1);
  const primaryChartPoints = buildLinePoints(salesSeries, 100, 44, 6);
  const primaryLinePath = buildSmoothPath(primaryChartPoints);
  const primaryAreaPath = `${primaryLinePath} L 94 38 L 6 38 Z`;
  const lastSalesPoint = primaryChartPoints.length > 0 ? primaryChartPoints[primaryChartPoints.length - 1] : undefined;
  const salesTicks = useMemo(
    () => buildAxisTicks(maxSalesValue, 4).map((tick) => ({ ...tick, label: `$${tick.label}` })),
    [maxSalesValue],
  );
  const ordersPreview = recentOrders.slice(0, 5);
  const nominees = (sideListItems ?? []).slice(0, 10);

  const accents = [
    { ring: 'bg-iw-cyan/15 text-iw-cyan', stroke: '#06B6D4', bar: 'from-iw-cyan/0 via-iw-cyan/80 to-iw-cyan' },
    { ring: 'bg-iw-purple/15 text-iw-purple', stroke: '#8B5CF6', bar: 'from-iw-purple/0 via-iw-purple/80 to-iw-purple' },
    { ring: 'bg-iw-orange/15 text-iw-orange', stroke: '#F1895C', bar: 'from-iw-orange/0 via-iw-orange/90 to-iw-orange' },
    { ring: 'bg-iw-teal/15 text-iw-teal', stroke: '#53B9C1', bar: 'from-iw-teal/0 via-iw-teal/80 to-iw-teal' },
  ] as const;

  const isNight = tone === 'night';
  const surface = isNight
    ? 'rounded-2xl border border-slate-800/80 bg-slate-950/35 backdrop-blur shadow-[0_18px_50px_-30px_rgba(0,0,0,0.85)]'
    : 'rounded-2xl border border-slate-200 bg-white shadow-[0_20px_50px_-38px_rgba(15,23,42,0.28)]';
  const heading = isNight ? 'text-slate-100' : 'text-slate-900';
  const muted = isNight ? 'text-slate-400' : 'text-slate-500';
  const panelBackground = isNight ? 'bg-slate-950/25 border-slate-800' : 'bg-slate-50 border-slate-200';
  const subtlePanel = isNight ? 'bg-slate-950/30 border-slate-800' : 'bg-white border-slate-200';
  const baselineStroke = isNight ? 'rgba(51,65,85,0.7)' : 'rgba(148,163,184,0.7)';
  const chartGridStroke = isNight ? 'rgba(51,65,85,0.7)' : 'rgba(226,232,240,1)';
  const pageGradient = isNight
    ? 'bg-[radial-gradient(circle_at_top,#1f2937_0%,#0b1224_46%,#070b16_100%)]'
    : 'bg-[radial-gradient(circle_at_top,#ffffff_0%,#f5f6fb_56%,#eef2ff_100%)]';

  const getCarouselStep = useCallback(() => {
    const container = carouselRef.current;
    if (!container) {
      return 320;
    }

    const firstItem = container.querySelector<HTMLElement>('[data-carousel-item]');
    if (firstItem) {
      const styles = window.getComputedStyle(container);
      const gap = Number.parseFloat(styles.columnGap || styles.gap || '0') || 0;
      return Math.max(240, Math.floor(firstItem.offsetWidth + gap));
    }

    return Math.max(260, Math.floor(container.clientWidth * 0.7));
  }, []);

  const scrollCarouselBy = useCallback(
    (direction: -1 | 1) => {
      const container = carouselRef.current;
      if (!container) return;
      container.scrollBy({ left: direction * getCarouselStep(), behavior: 'smooth' });
    },
    [getCarouselStep],
  );

  useEffect(() => {
    // Disabled auto-scrolling animation to keep overview charts static
    return undefined;
  }, [getCarouselStep, isCarouselHovered]);

  const getNomineesStep = useCallback(() => {
    const container = nomineesRef.current;
    if (!container) return 96;

    const firstItem = container.querySelector<HTMLElement>('[data-nominee-item]');
    if (firstItem) {
      const styles = window.getComputedStyle(container);
      const gap = Number.parseFloat(styles.rowGap || styles.gap || '0') || 0;
      return Math.max(72, Math.floor(firstItem.offsetHeight + gap));
    }

    return Math.max(72, Math.floor(container.clientHeight * 0.33));
  }, []);

  const scrollNomineesBy = useCallback(
    (direction: -1 | 1) => {
      const container = nomineesRef.current;
      if (!container) return;
      container.scrollBy({ top: direction * getNomineesStep(), behavior: 'smooth' });
    },
    [getNomineesStep],
  );

  useEffect(() => {
    // Disabled auto-scrolling animation to keep nominees list static
    return undefined;
  }, [getNomineesStep, isNomineesHovered]);

  return (
    <div className="space-y-4 pb-2">
      <div className={`rounded-2xl ${pageGradient} p-4 md:p-5`}>
        <div className="grid gap-4 lg:grid-cols-12">
          <section className="lg:col-span-5">
            <div className="mb-3 flex flex-wrap items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-slate-400">Overview</p>
                <h2 className={`mt-1 truncate text-2xl font-semibold ${heading}`}>{greeting}</h2>
              </div>
              {action}
            </div>

            {alertMessage ? (
              <div className={`mb-4 rounded-xl border px-3 py-2 text-sm ${subtlePanel} ${isNight ? 'text-slate-300' : 'text-slate-700'}`}>
                <span className="font-semibold text-iw-orange">Heads up:</span>{' '}
                <span className={isNight ? 'text-slate-300' : 'text-slate-600'}>{alertMessage}</span>
              </div>
            ) : null}

            <div className="grid gap-3 sm:grid-cols-2">
              {cards.map((card, index) => {
                const Icon = card.icon;
                const accent = accents[index % accents.length];
                const sparkSource = index % 2 === 0 ? volumeSeries : salesSeries;

                return (
                  <article key={card.id} className={`${surface} relative overflow-hidden p-4`}>
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className={`text-xs font-semibold ${muted}`}>{card.title}</p>
                        <p className={`mt-1 text-2xl font-semibold leading-none ${heading}`}>{card.value}</p>
                      </div>
                      <span className={`grid h-10 w-10 place-items-center rounded-2xl ${accent.ring}`}>
                        <Icon className="h-5 w-5" />
                      </span>
                    </div>

                    <div className="mt-3 flex items-end justify-between gap-2">
                      <div className="min-w-0">
                        <p className="text-xs font-semibold">
                          <span className={card.isPositive ? 'text-emerald-400' : 'text-rose-400'}>{card.trendValue}</span>{' '}
                          <span className={muted}>{card.trendLabel}</span>
                        </p>
                      </div>
                      <MiniSparkline values={sparkSource} stroke={accent.stroke} baselineStroke={baselineStroke} />
                    </div>

                    <div className={`absolute inset-x-0 bottom-0 h-1 bg-gradient-to-r ${accent.bar}`} aria-hidden="true" />
                  </article>
                );
              })}
            </div>
          </section>

          <section className="lg:col-span-7">
            <article className={`${surface} h-full p-4 md:p-5`}>
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <h3 className={`text-lg font-semibold ${heading}`}>Sales Volume (Payments)</h3>
                  <p className={`mt-1 text-xs ${muted}`}>Monthly trend of revenue collected.</p>
                </div>
                <div
                  className={`flex items-center gap-2 rounded-xl border px-2 py-1 text-xs font-semibold ${
                    isNight ? 'border-slate-800 bg-slate-950/30 text-slate-200' : 'border-slate-200 bg-white text-slate-700'
                  }`}
                >
                  <span className="inline-flex h-2 w-2 rounded-full bg-iw-purple" />
                  Weekly Sales
                </div>
              </div>

              <div className={`mt-4 rounded-2xl border p-3 ${panelBackground}`}>
                <p className={`mb-2 text-[10px] font-semibold uppercase tracking-[0.18em] ${isNight ? 'text-slate-400' : 'text-slate-500'}`}>
                  Y: Revenue · X: Months
                </p>
                <div className="relative">
                  <svg
                    ref={salesChartRef}
                    viewBox="0 0 100 44"
                    className="h-48 w-full md:h-56"
                    aria-label="Sales volume chart"
                  >
                  <defs>
                    <linearGradient id="haapa-sales-fill" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#8B5CF6" stopOpacity="0.35" />
                      <stop offset="100%" stopColor="#8B5CF6" stopOpacity="0" />
                    </linearGradient>
                  </defs>

                  <path d="M 6 6 V 38" stroke={chartGridStroke} strokeOpacity={isNight ? 0.75 : 0.9} strokeWidth="1" fill="none" />
                  <path d="M 6 38 H 94" stroke={chartGridStroke} strokeOpacity={isNight ? 0.75 : 0.9} strokeWidth="1" fill="none" />

                  {salesTicks.map((tick) => {
                    const y = 38 - tick.fraction * (38 - 6);
                    return (
                      <g key={tick.label}>
                        <path d={`M 6 ${y.toFixed(2)} H 94`} stroke={chartGridStroke} strokeOpacity={isNight ? 0.35 : 0.55} strokeWidth="1" fill="none" />
                        <path d={`M 6 ${y.toFixed(2)} H 4.7`} stroke={chartGridStroke} strokeOpacity={isNight ? 0.75 : 0.8} strokeWidth="1" fill="none" />
                        <text
                          x="4.1"
                          y={y.toFixed(2)}
                          textAnchor="end"
                          dominantBaseline="middle"
                          className={isNight ? 'fill-slate-400 text-[3.5px] font-semibold' : 'fill-slate-500 text-[3.5px] font-semibold'}
                        >
                          {tick.label}
                        </text>
                      </g>
                    );
                  })}

                  <path d={primaryAreaPath} className="haapa-chart-area" fill="url(#haapa-sales-fill)" />
                  <path
                    d={primaryLinePath}
                    className="haapa-chart-line opacity-90"
                    stroke="#8B5CF6"
                    strokeWidth="2.6"
                    fill="none"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    style={{ transition: 'none' }}
                  />
                </svg>
                </div>

                <div className="mt-3 grid grid-cols-12 text-center text-[10px] font-semibold uppercase tracking-wide text-slate-500">
                  {months.map((month, index) => (
                    <span
                      key={month}
                      className={`${index % 2 === 1 ? 'hidden sm:block' : ''}`}
                    >
                      {month}
                    </span>
                  ))}
                </div>
                <p className={`mt-2 text-center text-[10px] font-semibold ${isNight ? 'text-slate-400' : 'text-slate-500'}`}>Months</p>
              </div>
            </article>
          </section>
        </div>

        <div className="mt-4 grid gap-4 lg:grid-cols-12">
          {showRecentUsers ? (
            <article className={`${surface} lg:col-span-8 p-4 md:p-5`}>
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <h3 className={`text-lg font-semibold ${heading}`}>Recently Active Users</h3>
                  <p className={`mt-1 text-xs ${muted}`}>Quick access to the latest accounts.</p>
                </div>
                <a
                  href="/admin/dashboard/users"
                  className={`rounded-xl border px-3 py-2 text-xs font-semibold transition hover:border-iw-orange/60 hover:text-iw-orange ${
                    isNight ? 'border-slate-800 bg-slate-950/30 text-slate-200' : 'border-slate-200 bg-white text-slate-700'
                  }`}
                >
                  View More
                </a>
              </div>

              <div className="relative mt-4">
                <div
                  className={`pointer-events-none absolute inset-y-0 left-0 w-14 ${
                    isNight ? 'bg-gradient-to-r from-[#070b16] to-transparent' : 'bg-gradient-to-r from-white to-transparent'
                  }`}
                  aria-hidden="true"
                />
                <div
                  className={`pointer-events-none absolute inset-y-0 right-0 w-14 ${
                    isNight ? 'bg-gradient-to-l from-[#070b16] to-transparent' : 'bg-gradient-to-l from-white to-transparent'
                  }`}
                  aria-hidden="true"
                />

                <button
                  type="button"
                  onClick={() => scrollCarouselBy(-1)}
                  className={`absolute left-2 top-1/2 z-10 -translate-y-1/2 rounded-full border p-2 shadow-sm transition hover:scale-105 ${
                    isNight
                      ? 'border-slate-800 bg-slate-950/60 text-slate-200 hover:border-iw-purple/50'
                      : 'border-slate-200 bg-white/95 text-slate-700 hover:border-iw-purple/50'
                  }`}
                  aria-label="Scroll users left"
                >
                  <ChevronLeft className="h-4 w-4" />
                </button>
                <button
                  type="button"
                  onClick={() => scrollCarouselBy(1)}
                  className={`absolute right-2 top-1/2 z-10 -translate-y-1/2 rounded-full border p-2 shadow-sm transition hover:scale-105 ${
                    isNight
                      ? 'border-slate-800 bg-slate-950/60 text-slate-200 hover:border-iw-purple/50'
                      : 'border-slate-200 bg-white/95 text-slate-700 hover:border-iw-purple/50'
                  }`}
                  aria-label="Scroll users right"
                >
                  <ChevronRight className="h-4 w-4" />
                </button>

                <div
                  ref={carouselRef}
                  onMouseEnter={() => setIsCarouselHovered(true)}
                  onMouseLeave={() => setIsCarouselHovered(false)}
                  className="haapa-scrollbar-hidden flex gap-4 overflow-x-auto scroll-smooth pb-2 pt-1 snap-x snap-mandatory"
                >
                  {resolvedUsers.slice(0, 12).map((user) => (
                    <div
                      key={user.id}
                      data-carousel-item
                      className={`snap-start min-w-[240px] flex-1 rounded-2xl border px-4 py-5 text-center ${subtlePanel}`}
                    >
                      <div className="mx-auto h-20 w-20 overflow-hidden rounded-full border-4 border-white/10 bg-iw-purple/10">
                        {user.profileImageUrl ? (
                          <img src={user.profileImageUrl} alt={user.name} className="h-full w-full object-cover" />
                        ) : (
                          <div className="grid h-full w-full place-items-center text-2xl font-bold text-iw-purple">
                            {user.name.charAt(0).toUpperCase()}
                          </div>
                        )}
                      </div>

                      <p className={`mt-4 truncate text-base font-semibold ${heading}`}>{user.name}</p>
                      <p className={`mt-1 truncate text-xs ${muted}`}>{user.role}</p>

                      <div className="mt-4 flex items-center justify-center gap-3 text-xs font-semibold">
                        <span className={`inline-flex items-center gap-1 ${isNight ? 'text-slate-200' : 'text-slate-700'}`}>
                          <Star className="h-4 w-4 text-iw-orange" />
                          <span>{formatCompactNumber(ordersByBuyerId.get(user.id) ?? 0)}</span>
                          <span className={isNight ? 'text-slate-400' : 'text-slate-500'}>Orders</span>
                        </span>
                        <span className={isNight ? 'text-slate-400' : 'text-slate-500'}>|</span>
                        <a href="/admin/dashboard/users" className="text-iw-purple hover:underline">
                          View Profile
                        </a>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </article>
          ) : null}

          <article className={`${surface} lg:col-span-4 p-4 md:p-5`}>
            <div className="flex items-start justify-between gap-3">
              <div>
                <h3 className={`text-lg font-semibold ${heading}`}>{sideListTitle ?? 'Highlights'}</h3>
                <p className={`mt-1 text-xs ${muted}`}>Top-rated picks from your catalog.</p>
              </div>

              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => scrollNomineesBy(-1)}
                  className={`rounded-xl border p-2 text-xs font-semibold transition hover:scale-105 ${
                    isNight
                      ? 'border-slate-800 bg-slate-950/30 text-slate-200 hover:border-iw-purple/50'
                      : 'border-slate-200 bg-white text-slate-700 hover:border-iw-purple/50'
                  }`}
                  aria-label="Scroll nominees up"
                >
                  <ChevronUp className="h-4 w-4" />
                </button>
                <button
                  type="button"
                  onClick={() => scrollNomineesBy(1)}
                  className={`rounded-xl border p-2 text-xs font-semibold transition hover:scale-105 ${
                    isNight
                      ? 'border-slate-800 bg-slate-950/30 text-slate-200 hover:border-iw-purple/50'
                      : 'border-slate-200 bg-white text-slate-700 hover:border-iw-purple/50'
                  }`}
                  aria-label="Scroll nominees down"
                >
                  <ChevronDown className="h-4 w-4" />
                </button>
              </div>
            </div>

            <div className="relative mt-4">
              <div
                className={`pointer-events-none absolute inset-x-0 top-0 z-10 h-10 rounded-2xl ${
                  isNight ? 'bg-gradient-to-b from-[#070b16] to-transparent' : 'bg-gradient-to-b from-white to-transparent'
                }`}
                aria-hidden="true"
              />
              <div
                className={`pointer-events-none absolute inset-x-0 bottom-0 z-10 h-10 rounded-2xl ${
                  isNight ? 'bg-gradient-to-t from-[#070b16] to-transparent' : 'bg-gradient-to-t from-white to-transparent'
                }`}
                aria-hidden="true"
              />

              {nominees.length === 0 ? (
                <div
                  className={`rounded-2xl border px-3 py-6 text-center text-sm ${
                    isNight ? 'border-slate-800 bg-slate-950/30 text-slate-300' : 'border-slate-200 bg-white text-slate-600'
                  }`}
                >
                  No nominees available yet.
                </div>
              ) : (
                <div
                  ref={nomineesRef}
                  onMouseEnter={() => setIsNomineesHovered(true)}
                  onMouseLeave={() => setIsNomineesHovered(false)}
                  className="haapa-scrollbar-hidden flex max-h-[292px] flex-col gap-3 overflow-y-auto scroll-smooth pb-2 pt-2 snap-y snap-mandatory"
                >
                  {nominees.map((item) => (
                    <a
                      key={item.id}
                      data-nominee-item
                      href={item.href ?? '/admin/dashboard/books'}
                      className={`snap-start flex gap-3 rounded-2xl border p-3 transition hover:border-iw-purple/50 ${subtlePanel}`}
                    >
                      {item.imageUrl ? (
                        <img src={item.imageUrl} alt={item.title} className="h-12 w-9 rounded-lg object-cover" />
                      ) : (
                        <div
                          className={`grid h-12 w-9 place-items-center rounded-lg text-xs font-semibold ${
                            isNight ? 'bg-slate-900/70 text-slate-200' : 'bg-slate-100 text-slate-600'
                          }`}
                        >
                          BK
                        </div>
                      )}

                      <div className="min-w-0 flex-1">
                        <p className={`truncate text-sm font-semibold ${heading}`}>{item.title}</p>
                        {item.subtitle ? <p className={`truncate text-xs ${muted}`}>{item.subtitle}</p> : null}
                        <div className={`mt-2 flex items-center gap-3 text-[11px] font-semibold ${isNight ? 'text-slate-300' : 'text-slate-600'}`}>
                          {typeof item.rating === 'number' ? (
                            <span className="inline-flex items-center gap-1">
                              <Star className="h-3.5 w-3.5 text-iw-orange" />
                              {item.rating.toFixed(1)}
                            </span>
                          ) : null}
                          {typeof item.reviewCount === 'number' ? (
                            <span className={isNight ? 'text-slate-400' : 'text-slate-500'}>
                              {formatCompactNumber(item.reviewCount)} reviews
                            </span>
                          ) : null}
                        </div>
                      </div>
                    </a>
                  ))}
                </div>
              )}
            </div>
          </article>
        </div>

        <article className={`${surface} mt-4 p-4 md:p-5`}>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <h3 className={`text-lg font-semibold ${heading}`}>Book Addition Chart (Monthly)</h3>
              <p className={`mt-1 text-xs ${muted}`}>Monthly operational volume (orders).</p>
            </div>
          </div>

          <div className={`mt-4 rounded-2xl border p-3 ${panelBackground} transition-transform duration-500 hover:-translate-y-0.5`}>
            <p className={`mb-2 text-[10px] font-semibold uppercase tracking-[0.18em] ${isNight ? 'text-slate-400' : 'text-slate-500'}`}>
              Y: Orders · X: Months
            </p>
            <div className="grid grid-cols-[46px_minmax(0,1fr)] gap-3">
              <div className="flex h-32 flex-col justify-between pb-5 text-right text-[10px] font-semibold text-slate-500">
                {buildAxisTicks(maxVolumeValue, 4).map((tick) => (
                  <span key={tick.label} className={isNight ? 'text-slate-400' : 'text-slate-500'}>
                    {tick.label}
                  </span>
                ))}
              </div>

              <div className={`relative border-l ${isNight ? 'border-slate-800/80' : 'border-slate-200'}`}>
                <div className="absolute inset-x-0 top-0 flex h-32 flex-col justify-between pb-5" aria-hidden="true">
                  {buildAxisTicks(maxVolumeValue, 4).map((tick) => (
                    <div
                      key={`${tick.label}-line`}
                      className={`border-t ${isNight ? 'border-slate-800/80' : 'border-slate-200'}`}
                    />
                  ))}
                </div>
                <div className={`pointer-events-none absolute inset-x-0 bottom-5 border-b ${isNight ? 'border-slate-800/80' : 'border-slate-200'}`} aria-hidden="true" />

                <div className="relative grid h-32 grid-cols-12 items-end gap-2 pb-5">
                  {volumeSeries.map((value, index) => {
                    const heightPercent = Math.max(6, (value / maxVolumeValue) * 100);
                    return (
                      <div key={`${value}-${index}`} className="flex h-full items-end">
                        <div
                          className="haapa-bar-rise w-full rounded-md bg-iw-teal/80 transition-colors duration-500 hover:bg-iw-teal"
                          style={{ height: `${heightPercent}%`, animationDelay: `${index * 70}ms` }}
                          aria-hidden="true"
                        />
                      </div>
                    );
                  })}
                </div>

                <div className="grid grid-cols-12 text-center text-[10px] font-semibold uppercase tracking-wide text-slate-500">
                  {months.map((month, index) => (
                    <span key={month} className={index % 2 === 1 ? 'hidden sm:block' : ''}>
                      {month}
                    </span>
                  ))}
                </div>
                <p className={`mt-2 text-center text-[10px] font-semibold ${isNight ? 'text-slate-400' : 'text-slate-500'}`}>Months</p>
              </div>
            </div>
          </div>

          {showRecentOrders ? (
            <div
              className={`mt-4 rounded-2xl border p-3 text-sm ${panelBackground} ${isNight ? 'text-slate-200' : 'text-slate-700'}`}
            >
              {ordersPreview.length === 0 ? (
                <p className={isNight ? 'text-slate-300' : 'text-slate-500'}>No recent orders available.</p>
              ) : (
                <ul className="space-y-2">
                  {ordersPreview.map((order) => (
                    <li key={order.id} className="flex flex-wrap items-center justify-between gap-2">
                      <span className="font-semibold">{order.buyerName}</span>
                      <span className={`text-xs ${isNight ? 'text-slate-400' : 'text-slate-500'}`}>{formatOrderDate(order.createdAt)}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          ) : null}
        </article>
      </div>
    </div>
  );
};

const OverviewDashboard = ({
  greeting,
  alertMessage,
  statCards,
  performanceBars,
  performanceLine,
  recentOrders,
  users,
  action,
  tone = 'light',
  variant = 'classic',
  showUserDistribution = true,
  showRecentUsers = true,
  showRecentOrders = true,
  sideListTitle,
  sideListItems,
}: OverviewDashboardProps) => {
  if (variant === 'neo') {
    return (
      <NeoOverviewDashboard
        greeting={greeting}
        alertMessage={alertMessage}
        statCards={statCards}
        performanceBars={performanceBars}
        performanceLine={performanceLine}
        recentOrders={recentOrders}
        users={users}
        action={action}
        tone={tone}
        showRecentUsers={showRecentUsers}
        showRecentOrders={showRecentOrders}
        sideListTitle={sideListTitle}
        sideListItems={sideListItems}
      />
    );
  }

  const resolvedUsers = users ?? [];
  const cards = statCards.slice(0, 4);
  const bars = useMemo(() => {
    if (performanceBars.length >= 12) {
      return performanceBars.slice(0, 12);
    }

    const filled = [...performanceBars];
    while (filled.length < 12) {
      filled.push(0);
    }

    return filled.slice(0, 12);
  }, [performanceBars]);

  const maxBarValue = Math.max(...bars, 1);
  const ordersPreview = recentOrders.slice(0, 5);
  const adminCount = resolvedUsers.filter((u) => u.role === 'admin' || u.role === 'manager').length;
  const vendorCount = resolvedUsers.filter((u) => u.role === 'vendor').length;
  const buyerCount = resolvedUsers.filter((u) => u.role === 'buyer').length;
  const totalUsers = resolvedUsers.length;
  const adminPercent = totalUsers > 0 ? (adminCount / totalUsers) * 100 : 0;
  const vendorPercent = totalUsers > 0 ? (vendorCount / totalUsers) * 100 : 0;
  const showUserInsights = showUserDistribution || showRecentUsers;

  return (
    <div className="space-y-4 pb-2">
      <div className="grid gap-4 xl:grid-cols-[390px_minmax(0,1fr)]">
        <article className="rounded-2xl border border-slate-200 bg-white p-4 shadow-[0_18px_40px_-32px_rgba(15,23,42,0.48)]">
          <h2 className="text-2xl font-semibold text-slate-900">{greeting}</h2>
          <div className="mt-3 rounded-xl border border-orange-200 bg-orange-50 px-3 py-2 text-sm text-orange-700">
            {alertMessage}
          </div>

          <div className="mt-4 grid gap-3 min-[430px]:grid-cols-2">
            {cards.map((card) => {
              const Icon = card.icon;
              return (
                <article key={card.id} className="rounded-xl border border-slate-200 bg-white p-3">
                  <div className="flex items-start justify-between gap-2">
                    <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-orange-100 text-orange-600">
                      <Icon className="h-4 w-4" />
                    </span>
                    <p className="hidden text-xs font-semibold uppercase tracking-wide text-slate-400 sm:block">View More</p>
                  </div>
                  <p className="mt-3 text-xs font-semibold uppercase tracking-wide text-slate-500">{card.title}</p>
                  <p className="mt-1 text-3xl font-semibold leading-none text-slate-900">{card.value}</p>
                  <div className="mt-3 flex items-center gap-1 text-xs font-semibold">
                    {card.isPositive ? (
                      <ArrowUpRight className="h-3.5 w-3.5 text-emerald-600" />
                    ) : (
                      <ArrowDownRight className="h-3.5 w-3.5 text-rose-600" />
                    )}
                    <span className={card.isPositive ? 'text-emerald-600' : 'text-rose-600'}>{card.trendValue}</span>
                    <span className="text-slate-500">{card.trendLabel}</span>
                  </div>
                </article>
              );
            })}
          </div>
        </article>

        <article className="rounded-2xl border border-slate-200 bg-white p-4 shadow-[0_18px_40px_-32px_rgba(15,23,42,0.48)]">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h3 className="text-xl font-semibold text-slate-900">Performance</h3>
            <div className="flex items-center gap-1 rounded-lg border border-slate-200 p-1 text-xs font-semibold text-slate-500">
              {['ALL', '1M', '6M', '1Y'].map((periodLabel, index) => (
                <button
                  key={periodLabel}
                  type="button"
                  className={`rounded-md px-2 py-1 ${index === 3 ? 'bg-orange-500 text-white' : 'hover:bg-orange-50'}`}
                >
                  {periodLabel}
                </button>
              ))}
            </div>
          </div>

          <div className="relative mt-4 h-64 rounded-xl border border-slate-200 bg-slate-50 px-2 pb-8 pt-4 sm:h-72 sm:px-3">
            <div className="absolute inset-x-3 bottom-8 top-4 grid grid-cols-12 items-end gap-1.5">
              {bars.map((value, index) => {
                const barHeight = Math.max(6, (value / maxBarValue) * 100);
                return (
                  <div key={`${value}-${index}`} className="flex h-full items-end">
                    <div
                      className="w-full rounded-sm bg-orange-500"
                      style={{ height: `${barHeight}%` }}
                    />
                  </div>
                );
              })}
            </div>

            <div className="absolute inset-x-3 bottom-2 grid grid-cols-12 text-center text-[10px] font-semibold uppercase tracking-wide text-slate-400">
              {months.map((month, index) => (
                <span key={month} className={index % 2 === 1 ? 'hidden sm:block' : ''}>
                  {month}
                </span>
              ))}
            </div>
          </div>

          <div className="mt-2 flex items-center gap-4 text-xs font-semibold">
            <span className="inline-flex items-center gap-1 text-orange-600">
              <span className="h-2 w-2 rounded-full bg-orange-500" />
              Page Views
            </span>
            <span className="inline-flex items-center gap-1 text-emerald-600">
              <span className="h-2 w-2 rounded-full bg-emerald-500" />
              Clicks
            </span>
          </div>
        </article>
      </div>

      {showUserInsights && (
        <div className={`grid gap-4 ${showUserDistribution && showRecentUsers ? 'md:grid-cols-2' : 'md:grid-cols-1'}`}>
          {showUserDistribution && (
            <article className="rounded-2xl border border-slate-200 bg-white p-4 shadow-[0_18px_40px_-32px_rgba(15,23,42,0.48)]">
              <h3 className="text-lg font-semibold text-slate-900">User Distribution</h3>
              <div className="mt-5 flex justify-center">
                <div
                  className="grid h-40 w-40 place-items-center rounded-full transition-all duration-500 hover:scale-105 cursor-pointer"
                  style={{
                    background: `conic-gradient(
                      #ef4444 0% ${adminPercent}%,
                      #f59e0b ${adminPercent}% ${adminPercent + vendorPercent}%,
                      #10b981 ${adminPercent + vendorPercent}% 100%
                    )`,
                  }}
                >
                  <div className="grid h-28 w-28 place-items-center rounded-full bg-white text-center shadow-lg">
                    <p className="text-2xl font-semibold text-slate-900 transition-all duration-300">{totalUsers}</p>
                    <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">Total Users</p>
                  </div>
                </div>
              </div>
              <div className="mt-5 grid grid-cols-3 gap-2 text-center">
                <div className="rounded-xl bg-red-50 p-2 transition-all duration-300 hover:scale-105 hover:shadow-md cursor-pointer">
                  <p className="text-[11px] font-semibold uppercase tracking-wide text-red-600">Admins</p>
                  <p className="text-xl font-semibold text-slate-900">{adminCount}</p>
                </div>
                <div className="rounded-xl bg-amber-50 p-2 transition-all duration-300 hover:scale-105 hover:shadow-md cursor-pointer">
                  <p className="text-[11px] font-semibold uppercase tracking-wide text-amber-600">Vendors</p>
                  <p className="text-xl font-semibold text-slate-900">{vendorCount}</p>
                </div>
                <div className="rounded-xl bg-emerald-50 p-2 transition-all duration-300 hover:scale-105 hover:shadow-md cursor-pointer">
                  <p className="text-[11px] font-semibold uppercase tracking-wide text-emerald-600">Buyers</p>
                  <p className="text-xl font-semibold text-slate-900">{buyerCount}</p>
                </div>
              </div>
              <a
                href="/admin/dashboard/users"
                className="mt-4 block w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-center text-sm font-semibold text-slate-600 transition hover:bg-orange-50 hover:text-orange-700"
              >
                View All Users
              </a>
            </article>
          )}

          {showRecentUsers && (
            <article className="rounded-2xl border border-slate-200 bg-white p-4 shadow-[0_18px_40px_-32px_rgba(15,23,42,0.48)]">
              <div className="flex items-center justify-between gap-2">
                <h3 className="text-lg font-semibold text-slate-900">Recent Users</h3>
                <a
                  href="/admin/dashboard/users"
                  className="rounded-lg border border-slate-200 px-2.5 py-1 text-xs font-semibold text-slate-600 transition hover:bg-orange-50 hover:text-orange-700"
                >
                  View All
                </a>
              </div>
              <div className="mt-3 space-y-2">
                {resolvedUsers.slice(0, 5).map((user, index) => (
                  <div
                    key={user.id}
                    className="flex items-center gap-3 rounded-xl border border-slate-200 bg-slate-50 p-2 transition-all duration-300 hover:scale-[1.02] hover:shadow-md hover:border-orange-300 cursor-pointer"
                    style={{ animationDelay: `${index * 100}ms` }}
                  >
                    {user.profileImageUrl ? (
                      <img src={user.profileImageUrl} alt={user.name} className="h-10 w-10 rounded-full object-cover transition-transform duration-300 hover:scale-110" />
                    ) : (
                      <div className="flex h-10 w-10 items-center justify-center rounded-full bg-orange-200 text-xs font-bold text-orange-800 transition-transform duration-300 hover:scale-110">
                        {user.name.charAt(0).toUpperCase()}
                      </div>
                    )}
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-semibold text-slate-900">{user.name}</p>
                      <p className="truncate text-xs text-slate-500">{user.email}</p>
                    </div>
                    <span className={`rounded-full px-2 py-1 text-[10px] font-semibold transition-all duration-300 hover:scale-110 ${
                      user.role === 'admin' ? 'bg-red-100 text-red-700' :
                      user.role === 'manager' ? 'bg-sky-100 text-sky-700' :
                      user.role === 'vendor' ? 'bg-amber-100 text-amber-700' :
                      'bg-emerald-100 text-emerald-700'
                    }`}>
                      {user.role === 'admin' ? 'Admin' : user.role === 'manager' ? 'Manager' : user.role === 'vendor' ? 'Vendor' : 'Buyer'}
                    </span>
                  </div>
                ))}
              </div>
            </article>
          )}
        </div>
      )}

      {showRecentOrders && (
        <article className="rounded-2xl border border-slate-200 bg-white p-4 shadow-[0_18px_40px_-32px_rgba(15,23,42,0.48)]">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h3 className="text-lg font-semibold text-slate-900">Recent Orders</h3>
            {action}
          </div>
          <div className="mt-3 overflow-x-auto rounded-xl border border-slate-200">
            <table className="min-w-[860px] divide-y divide-slate-200 lg:min-w-[980px]">
              <thead className="bg-slate-50">
                <tr>
                  <th className="px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-wide text-slate-500">Customer Name</th>
                  <th className="px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-wide text-slate-500">Date</th>
                  <th className="px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-wide text-slate-500">Product</th>
                  <th className="px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-wide text-slate-500">Email</th>
                  <th className="px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-wide text-slate-500">Phone No.</th>
                  <th className="px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-wide text-slate-500">Address</th>
                  <th className="px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-wide text-slate-500">Payment Type</th>
                  <th className="px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-wide text-slate-500">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 text-sm text-slate-700">
                {ordersPreview.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="px-3 py-8 text-center text-sm text-slate-500">
                      No recent orders available.
                    </td>
                  </tr>
                ) : (
                  ordersPreview.map((order) => {
                    const leadItem = order.items[0];
                    const leadProduct = leadItem?.product;
                    const addressLabel = order.shippingAddress.city;

                    return (
                      <tr key={order.id}>
                        <td className="px-3 py-2 text-xs font-semibold text-slate-700">{order.buyerName}</td>
                        <td className="px-3 py-2 text-xs text-slate-600">{formatOrderDate(order.createdAt)}</td>
                        <td className="px-3 py-2">
                          {leadProduct ? (
                            <img src={leadProduct.imageUrl} alt={leadProduct.nameKey} className="h-7 w-7 rounded-md object-cover" />
                          ) : (
                            <span className="text-xs text-slate-400">-</span>
                          )}
                        </td>
                        <td className="px-3 py-2 text-xs text-slate-600">{order.buyerEmail}</td>
                        <td className="px-3 py-2 text-xs text-slate-600">{order.shippingAddress.phone || '-'}</td>
                        <td className="px-3 py-2 text-xs text-slate-600">{addressLabel}</td>
                        <td className="px-3 py-2 text-xs font-medium text-slate-700">{paymentLabel[order.paymentMethod]}</td>
                        <td className="px-3 py-2">
                          <span className={`inline-flex rounded-md px-2 py-1 text-[11px] font-semibold ${statusClasses[order.status]}`}>
                            {order.status}
                          </span>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
          <div className="mt-3 flex flex-wrap items-center justify-between gap-2">
            <p className="text-xs text-slate-500">
              Showing {ordersPreview.length} of {recentOrders.length} orders
            </p>
            <div className="flex items-center overflow-hidden rounded-lg border border-slate-200 text-xs font-semibold text-slate-600">
              <button type="button" className="px-3 py-2 transition hover:bg-orange-50 hover:text-orange-700">Previous</button>
              <button type="button" className="border-l border-slate-200 bg-orange-500 px-3 py-2 text-white">1</button>
              <button type="button" className="border-l border-slate-200 px-3 py-2 transition hover:bg-orange-50 hover:text-orange-700">2</button>
              <button type="button" className="border-l border-slate-200 px-3 py-2 transition hover:bg-orange-50 hover:text-orange-700">3</button>
              <button type="button" className="border-l border-slate-200 px-3 py-2 transition hover:bg-orange-50 hover:text-orange-700">Next</button>
            </div>
          </div>
        </article>
      )}
    </div>
  );
};

export default OverviewDashboard;
